/**
 * Normalize a string into a URL-friendly slug.
 *
 * Behavior:
 * - Converts to lowercase.
 * - Removes accents/diacritics.
 * - Removes special characters.
 * - Replaces whitespace with hyphens.
 * - Preserves intermediate states while typing.
 *
 * @param {string} value - Raw input value.
 * @returns {string} Slugified string.
 */
function normalizeSlugInput(value) {
    if (!value) return '';
    value = String(value);

    return value
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^\w\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-"); // solo colapsa, NO trim
}

/**
 * Finalize a slug by trimming leading/trailing hyphens.
 *
 * @param {string} value - Input slug value.
 * @returns {string} Finalized slug.
 */
function finalizeSlug(value) {
    if (!value) return '';
    return value.replace(/^-+|-+$/g, '');
}

/**
 * Vue directive that enforces a slug format on an input element.
 *
 * Important:
 * - Works with strings only.
 * - Dispatches a native `input` event when it mutates the value
 *   so `v-model` stays in sync.
 * - Should NOT be combined with `v-model.trim` or `.number`.
 *
 * @type {Object}
 */
export default {
    /**
     * Called when the directive is bound to the element.
     *
     * @param {HTMLElement} el - The element the directive is bound to (input expected).
     */
    bind(el) {
        el.addEventListener("input", (event) => {
            const input = event.target;
            const formatted = normalizeSlugInput(input.value);

            if (formatted !== input.value) {
                const cursorPos = input.selectionStart;

                input.value = formatted;

                try {
                    input.setSelectionRange(cursorPos, cursorPos);
                } catch (e) {
                    // Ignore errors for unsupported input types
                }

                // Notify Vue that the value changed
                input.dispatchEvent(new Event('input'));
            }
        });

        el.addEventListener("focusout", (event) => {
            const input = event.target;
            const finalized = finalizeSlug(input.value);
            if (finalized !== input.value) {
                input.value = finalized;
                input.dispatchEvent(new Event('input'));
            }
        });
    },
};
